import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil, filter } from 'rxjs';
import { assetsConst } from 'src/app/core/const/assets.const';
import { responseStatus } from 'src/app/core/const/responseStatus.const';
import { AuthService } from 'src/app/core/services/auth.service';
import { SocketService } from 'src/app/core/services/socket.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { TableTooltipService } from 'src/app/core/services/table-tooltip.service';
import { LoginResponse } from 'src/app/models/loginResponse';
import { logoutResponse } from 'src/app/models/logoutResponse';
import { environment } from 'src/environments/environment';
import { MatSidenav } from '@angular/material/sidenav';
import { InventoryService } from 'src/app/core/services/inventory.service';
import { ProductService } from 'src/app/core/services/product.service';
import { interval } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Notification } from 'src/app/models/notification';
import {
  AppLanguage,
  LanguageService,
  SUPPORTED_LANGUAGES
} from 'src/app/core/services/language.service';

interface LowStockItem {
  productId: string;
  variantId: string;
  factoryId: string;
  availableQuantity: number;
  productName?: string;
  vendorName?: string;
  threshold?: number;
}

interface LapsingProductResponse {
  statusCode: number;
  message: string;
  data: {
    vendors: Array<{
      _id: string;
      vendorName: string;
      products: Array<{
        productId: string;
        variantId: string;
        factoryId: string;
        availableQuantity: number;
      }>;
    }>;
    totalVendors: number;
    page: number;
    limit: number;
  };
  status: number;
}

@Component({
  selector: 'megastore-admin-after-login',
  templateUrl: './after-login.component.html',
  styleUrls: ['./after-login.component.scss']
})
export class AfterLoginComponent implements OnInit, OnDestroy {
  assetsConst = assetsConst;
  sidenavWidth = 20;
  destroy$: Subject<boolean> = new Subject<boolean>();
  subscriptionSubject$!: Subscription;
  profile: LoginResponse | null = null;
  audio = new Audio();
  lowStockItems: LowStockItem[] = [];
  lowStockCount: number = 0;
  private readonly LOW_STOCK_THRESHOLD = 10; // You can adjust this threshold
  
  // Notifications
  notifications: Notification[] = [];
  notificationCount: number = 0;

  // Language
  languages = SUPPORTED_LANGUAGES;
  currentLanguage: AppLanguage = 'en';

  constructor(
    public _authService: AuthService,
    private router: Router,
    private _spinnerService: SpinnerService,
    private socketService: SocketService,
    private _toasterService: ToasterService,
    private translateService: TranslateService,
    private _inventoryService: InventoryService,
    private _productService: ProductService,
    private _notificationService: NotificationService,
    private _tableTooltipService: TableTooltipService,
    private _languageService: LanguageService
  ) {
    this.socketService.initSocket();
    this._languageService.language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => (this.currentLanguage = language));
  }

  changeLanguage(language: AppLanguage): void {
    this._languageService.switchTo(language);
  }
  
  ngOnInit(): void {
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profile = res;
        } else {
          this.getProfile();
        }
      });
    this.handleListenersAndEmitters();
    this.loadLowStockItems();
    this.loadNotifications();
    
    // Initialize table tooltips
    this._tableTooltipService.initializeTableTooltips();
    
    // Listen for route changes to refresh tooltips
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Refresh tooltips after route navigation
        setTimeout(() => {
          this._tableTooltipService.refreshTooltips();
        }, 500);
      });
  }

  handleListenersAndEmitters(): void {
    this.socketService
      .onEvent('newOrderOk')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.playAudio();
          this._toasterService.successToastr(
            this.translateService.instant('orderTempl')
          );
        }
      });
  }

  playAudio(): void {
    this.audio.src = assetsConst.soundFile;
    this.audio.load();
    this.audio.play();
    this.audio.muted = false;
  }

  getProfile(): void {
    this._spinnerService.addToLoader('getProfile');
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: LoginResponse) => {
          this._spinnerService.removeFromLoader('getProfile');
          this.profile = res;
          this._authService.profileDetails.next(res);
        },
        error: () => {
          this._spinnerService.removeFromLoader('getProfile');
        }
      });
  }

  toggleSidenav(): void {
    this._authService.toggle();
  }

  logout(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._authService.logoutAlert().then((res: any) => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        this._authService
          .logout()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: logoutResponse) => {
              if (res.statusCode === responseStatus.SUCCESS) {
                localStorage.removeItem(environment.storageKey);
                this.router.navigate(['/auth/login']);
              }
            }
          });
      }
    });
  }

  loadLowStockItems(): void {
    this._inventoryService.getLapsingProducts().subscribe({
      next: (response: LapsingProductResponse) => {
        if (response.statusCode === 200 && response.data.vendors) {
          // Flatten and process the data
          const allProducts: LowStockItem[] = [];
          
          response.data.vendors.forEach(vendor => {
            vendor.products.forEach(product => {
              allProducts.push({
                ...product,
                vendorName: vendor.vendorName,
                threshold: this.LOW_STOCK_THRESHOLD
              });
            });
          });

          // Sort by quantity and take top 10
          this.lowStockItems = allProducts
            .sort((a, b) => a.availableQuantity - b.availableQuantity)
            .slice(0, 10);

          // Update count
          this.lowStockCount = this.lowStockItems.length;

          // Load product names
          // this.loadProductNames();
        }
      },
      error: (error) => {
        console.error('Error loading low stock items:', error);
      }
    });
  }

  private loadProductNames() {
    // Load product names for each item
    this.lowStockItems.forEach(item => {
      this._productService.getProductById(item.productId).subscribe({
        next: (product) => {
          if (product) {
            item.productName = product.name;
          }
        },
        error: (error) => {
          console.error('Error loading product name:', error);
        }
      });
    });
  }

  loadNotifications(): void {
    const query = `page=1&limit=5`; // Load only 5 most recent notifications
    this._spinnerService.addToLoader('getNotifications');
    this._notificationService
      .getNotificationList(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this._spinnerService.removeFromLoader('getNotifications');
          this.notifications = res.notification;
          this.notificationCount = res.notificationCount;
        },
        error: (error) => {
          this._spinnerService.removeFromLoader('getNotifications');
          console.error('Error loading notifications:', error);
        }
      });
  }

  getTimeAgo(createdAt: string): string {
    const created = new Date(createdAt);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - created.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
    this.subscriptionSubject$.unsubscribe();
  }
}
